import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './ConfirmModal';
import { API } from '../api/api';

const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

// Ferieafrunding - afrund til nærmeste halve eller hele arbejdsdag
const applyVacationRounding = (hours, timecodeName) => {
  // Check if timecode contains "FER" (case insensitive)
  if (timecodeName && timecodeName.toUpperCase().includes('FER')) {
    // Round to either 3.75 (half day) or 7.5 (full day)
    // Midpoint at 5.625 hours = (3.75 + 7.5) / 2
    return hours <= 5.625 ? 3.75 : 7.5;
  }
  return hours; // No rounding for non-vacation codes
};

export default function SeriesModal({ 
  isOpen, 
  onClose, 
  onCreateSeries, 
  templateEvent, 
  projects 
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [projectId, setProjectId] = useState('');
  const [timecodeId, setTimecodeId] = useState('');
  const [notes, setNotes] = useState('');
  const [billingtype, setBillingType] = useState('');
  const [projectTimecodes, setProjectTimecodes] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [vacationRoundingMessage, setVacationRoundingMessage] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountProjects, setAccountProjects] = useState([]);
  const [defaultProjectId, setDefaultProjectId] = useState('');
  const [isLoadingAccountProjects, setIsLoadingAccountProjects] = useState(false);

  // Account search for AsyncSelect (debounced)
  const accountDebounceRef = React.useRef(null);
  const loadAccountOptions = (inputValue, callback) => {
    clearTimeout(accountDebounceRef.current);
    const delay = inputValue ? 350 : 0; // No delay for initial load
    accountDebounceRef.current = setTimeout(async () => {
      try {
        const data = await API.searchAccounts(inputValue || '');
        callback(data.success ? data.data.map(a => ({ value: a.id, label: a.name })) : []);
      } catch {
        callback([]);
      }
    }, delay);
  };

  // Fetch projects linked to selected account
  useEffect(() => {
    if (accountId) {
      setIsLoadingAccountProjects(true);
      setProjectId('');
      setTimecodeId('');
      setProjectTimecodes([]);
      API.fetchAccountProjects(accountId)
        .then(data => {
          if (data.success) {
            setAccountProjects(data.data);
            setDefaultProjectId(data.defaultProjectId || '');
          } else {
            setAccountProjects([]);
            setDefaultProjectId('');
          }
        })
        .catch(() => {
          setAccountProjects([]);
          setDefaultProjectId('');
        })
        .finally(() => setIsLoadingAccountProjects(false));
    } else {
      setAccountProjects([]);
      setDefaultProjectId('');
    }
  }, [accountId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen && templateEvent) {
      // Pre-fill with template event data
      setStartDate(moment().format('YYYY-MM-DD')); // Start from today
      setEndDate(moment().add(1, 'month').format('YYYY-MM-DD')); // Default 1 month
      
      // Extract start and end times from template event
      setStartTime(moment(templateEvent.start).format('HH:mm'));
      setEndTime(moment(templateEvent.end).format('HH:mm'));
      
      setProjectId(templateEvent.projectId || '');
      setTimecodeId(templateEvent.timecodeId || '');
      // Set default notes if coming from header (empty template)
      setNotes(templateEvent.title || (templateEvent.projectId ? '' : 'Vacation/Leave'));
      setBillingType(templateEvent.billingtype || 'Billable');
      setSkipWeekends(true);
    } else if (!isOpen) {
      // Reset form when closed
      resetForm();
    }
  }, [isOpen, templateEvent]);

  // Fetch timecodes when project changes
  useEffect(() => {
    if (projectId) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/get-tasks-by-project?projectId=${projectId}`, {
        credentials: 'include', // send session cookie so ensureZohoAccessToken passes
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setProjectTimecodes(data.data);
          } else {
            setProjectTimecodes([]);
          }
        })
        .catch(() => {
          setProjectTimecodes([]);
        });
    } else {
      setProjectTimecodes([]);
    }
  }, [projectId]);

  // Auto-set billing type when timecode changes
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      setBillingType(selected.billingtype || '');
    }
  }, [timecodeId, projectTimecodes]);

  // Check for vacation rounding when time or timecode changes
  useEffect(() => {
    if (startTime && endTime && timecodeId) {
      try {
        const startHours = parseHHMMToDecimalHours(startTime);
        const endHours = parseHHMMToDecimalHours(endTime);
        const currentHours = endHours - startHours;
        
        if (currentHours <= 0) {
          setVacationRoundingMessage('');
          return;
        }
        
        const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
        const timecodeName = selectedTimecode?.name || '';
        
        if (timecodeName.toUpperCase().includes('FER')) {
          const roundedHours = applyVacationRounding(currentHours, timecodeName);
          if (roundedHours !== currentHours) {
            const dayType = roundedHours === 3.75 ? 'half day' : 'full day';
            setVacationRoundingMessage(`Holiday hours will be rounded to ${formatDecimalHoursToHHMM(roundedHours)} hours (${dayType})`);
          } else {
            setVacationRoundingMessage('');
          }
        } else {
          setVacationRoundingMessage('');
        }
      } catch (e) {
        setVacationRoundingMessage('');
      }
    } else {
      setVacationRoundingMessage('');
    }
  }, [startTime, endTime, timecodeId, projectTimecodes]);

  // Auto-fill notes based on worktype selection (same as EventFormModal)
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      // Skip auto-fill if this is based on an existing entry with the same timecode (preserve original notes)
      if (templateEvent && templateEvent.timecodeId === timecodeId && templateEvent.title && templateEvent.title.trim()) {
        return; // Don't overwrite original notes from template event
      }

      if (selected.billingtype === 'Non Billable') {
        // Set notes to worktype name for non-billable tasks
        const workTypeName = selected.name || '';
        const currentNotes = notes;
        
        // Check if current notes contains "|" separator
        if (currentNotes.includes(' | ')) {
          // Replace only the worktype part (before "|"), keep user text after "|"
          const userText = currentNotes.split(' | ').slice(1).join(' | ');
          setNotes(`${workTypeName} | ${userText}`);
        } else {
          // For Non-Billable, just set worktype name (no preservation of old text)
          setNotes(workTypeName);
        }
      } else {
        // For billable tasks, only clear if it's a worktype-generated note
        if (notes.includes(' | ') || !notes.trim()) {
          setNotes('');
        }
        // If user has written custom text without "|", preserve it
      }
    }
  }, [timecodeId, projectTimecodes, templateEvent, notes]);

  // ESC key listener (same as EventFormModal)
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges]);

  // Track changes in form fields (same as EventFormModal)
  useEffect(() => {
    if (templateEvent && isOpen) {
      setHasChanges(
        startDate !== moment().format('YYYY-MM-DD') ||
        endDate !== moment().add(1, 'month').format('YYYY-MM-DD') ||
        startTime !== moment(templateEvent.start).format('HH:mm') ||
        endTime !== moment(templateEvent.end).format('HH:mm') ||
        projectId !== (templateEvent.projectId || '') ||
        timecodeId !== (templateEvent.timecodeId || '') ||
        notes !== (templateEvent.title || (templateEvent.projectId ? '' : 'Vacation/Leave')) ||
        billingtype !== (templateEvent.billingtype || 'Billable') ||
        !skipWeekends // Default is true, so any change from true is a change
      );
    }
  }, [startDate, endDate, startTime, endTime, projectId, timecodeId, notes, billingtype, skipWeekends, templateEvent, isOpen]);

  const resetForm = () => {
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    setSkipWeekends(true);
    setProjectId('');
    setTimecodeId('');
    setNotes('');
    setBillingType('');
    setProjectTimecodes([]);
    setErrors({});
    setHasSubmitted(false);
    setIsCreating(false);
    setProgress({ current: 0, total: 0 });
    setHasChanges(false);
    setShowDiscardModal(false);
    setVacationRoundingMessage('');
    setAccountId('');
    setAccountName('');
    setAccountProjects([]);
    setDefaultProjectId('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!startDate) newErrors.startDate = "Please select a start date";
    if (!endDate) newErrors.endDate = "Please select an end date";
    if (!startTime) newErrors.startTime = "Please enter a start time";
    if (!endTime) newErrors.endTime = "Please enter an end time";
    if (!accountId) newErrors.accountId = "Please select an account";
    if (!projectId && !defaultProjectId) newErrors.projectId = "No default project found for this account";
    if (!timecodeId) newErrors.timecodeId = "Please select a worktype";
    if (!notes.trim()) newErrors.notes = "Please enter notes";

    if (startDate && endDate) {
      if (moment(endDate).isBefore(moment(startDate))) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    // Validate time format and logic
    if (startTime && endTime) {
      const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timePattern.test(startTime)) {
        newErrors.startTime = "Invalid time format";
      }
      if (!timePattern.test(endTime)) {
        newErrors.endTime = "Invalid time format";
      }
      
      if (timePattern.test(startTime) && timePattern.test(endTime)) {
        const startHours = parseHHMMToDecimalHours(startTime);
        const endHours = parseHHMMToDecimalHours(endTime);
        const duration = endHours - startHours;
        
        if (duration <= 0) {
          newErrors.endTime = "End time must be after start time";
        } else if (duration > 23) {
          newErrors.endTime = "Duration cannot exceed 23 hours";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateDateRange = () => {
    const dates = [];
    const start = moment(startDate);
    const end = moment(endDate);
    
    let current = start.clone();
    while (current.isSameOrBefore(end)) {
      // Skip weekends if option is selected
      if (!skipWeekends || (current.day() !== 0 && current.day() !== 6)) {
        dates.push(current.clone());
      }
      current.add(1, 'day');
    }
    
    return dates;
  };

  const handleSubmit = async () => {
    setHasSubmitted(true);
    if (!validateForm()) return;

    const dates = generateDateRange();
    if (dates.length === 0) {
      setErrors({ general: 'No valid dates found in the selected range' });
      return;
    }

    // Show confirmation with date count
    setShowConfirmModal(true);
  };

  const handleConfirmCreate = async () => {
    setShowConfirmModal(false);
    setIsCreating(true);
    
    const dates = generateDateRange();
    setProgress({ current: 0, total: dates.length });

    try {
      // Calculate duration from start and end times
      let duration;
      try {
        const startHours = parseHHMMToDecimalHours(startTime);
        const endHours = parseHHMMToDecimalHours(endTime);
        duration = endHours - startHours;
        
        if (duration <= 0) {
          setErrors({ endTime: 'End time must be after start time' });
          setIsCreating(false);
          return;
        }
      } catch (e) {
        setErrors({ general: 'Invalid time format' });
        setIsCreating(false);
        return;
      }

      // Apply vacation rounding for FER timecodes
      const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
      const timecodeName = selectedTimecode?.name || '';
      duration = applyVacationRounding(duration, timecodeName);

      // Generate unique series ID for tracking
      const seriesId = `series-${Date.now()}`;
      
      const eventsToCreate = dates.map(date => {
        // Use the specific start and end times from the form
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        const eventStart = moment(date).hours(startHour).minutes(startMinute);
        const eventEnd = moment(date).hours(endHour).minutes(endMinute);
        
        // For vacation rounding, adjust the end time if duration was changed
        if (timecodeName.toUpperCase().includes('FER')) {
          const originalDuration = parseHHMMToDecimalHours(endTime) - parseHHMMToDecimalHours(startTime);
          if (duration !== originalDuration) {
            // Recalculate end time based on rounded duration
            const adjustedEnd = eventStart.clone().add(duration, 'hours');
            eventEnd.hours(adjustedEnd.hours()).minutes(adjustedEnd.minutes());
          }
        }
        
        const resolvedProjectId = projectId || defaultProjectId;
        return {
          title: notes,
          start: eventStart.toDate(),
          end: eventEnd.toDate(),
          projectId: resolvedProjectId,
          timecodeId,
          billingtype,
          accountId,
          accountName,
          projectName: projectOptions.find(p => p.value === resolvedProjectId)?.label || accountName || '',
          timecodeName: timecodeOptions.find(tc => tc.value === timecodeId)?.label || '',
          seriesId: seriesId, // Track which series this belongs to
        };
      });

      // Call the parent function to create the series
      await onCreateSeries(eventsToCreate, setProgress);
      
      // Close modal on success
      onClose();
      resetForm();
    } catch (error) {
      setErrors({ general: 'Failed to create series. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (isCreating) return; // Don't allow closing during creation
    
    if (hasChanges) {
      setShowDiscardModal(true);
    } else {
      onClose();
      resetForm();
    }
  };

  const handleConfirmClose = () => {
    setShowDiscardModal(false);
    onClose();
    resetForm();
  };

  const handleCancelClose = () => {
    setShowDiscardModal(false);
  };

  if (!isOpen) return null;

  const projectOptions = (accountId ? accountProjects : projects).map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const timecodeOptions = projectTimecodes.map((tc) => ({
    value: tc.id,
    label: tc.name,
  }));

  const dateCount = generateDateRange().length;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2500] p-4"
        onClick={handleClose}
      >
        <div 
          className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Create Series</h2>
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            {errors.general && (
              <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded p-3">
                {errors.general}
              </div>
            )}

            {isCreating && (
              <div className="bg-gray-50 border border-gray-200 rounded p-3">
                <div className="text-sm text-gray-600 mb-2">
                  Creating entries... {progress.current} of {progress.total}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(progress.current / progress.total) * 100}%`,
                      backgroundColor: '#ccb778' 
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isCreating}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                />
                {hasSubmitted && errors.startDate && (
                  <div className="text-red-500 text-sm mt-1">{errors.startDate}</div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isCreating}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                />
                {hasSubmitted && errors.endDate && (
                  <div className="text-red-500 text-sm mt-1">{errors.endDate}</div>
                )}
              </div>
            </div>

                         {/* Time Range */}
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                 <input
                   type="time"
                   value={startTime}
                   onChange={(e) => setStartTime(e.target.value)}
                   disabled={isCreating}
                   className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                 />
                 {hasSubmitted && errors.startTime && (
                   <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>
                 )}
               </div>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                 <input
                   type="time"
                   value={endTime}
                   onChange={(e) => setEndTime(e.target.value)}
                   disabled={isCreating}
                   className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                 />
                 {hasSubmitted && errors.endTime && (
                   <div className="text-red-500 text-sm mt-1">{errors.endTime}</div>
                 )}
               </div>
             </div>
             
             {/* Vacation Rounding Message */}
             {vacationRoundingMessage && (
               <div className="text-gray-500 text-xs font-medium bg-gray-50 border border-gray-200 rounded p-2">
                 {vacationRoundingMessage}
               </div>
             )}

            {/* Skip Weekends */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={skipWeekends}
                  onChange={(e) => setSkipWeekends(e.target.checked)}
                  disabled={isCreating}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">Skip weekends</span>
              </label>
            </div>

            {/* Date Count Preview */}
            {startDate && endDate && (
              <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-3">
                📅 Will create <strong>{dateCount}</strong> time registration{dateCount !== 1 ? 's' : ''}
              </div>
            )}

            {/* Account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
              <AsyncSelect
                loadOptions={loadAccountOptions}
                defaultOptions
                cacheOptions
                value={accountId ? { value: accountId, label: accountName } : null}
                onChange={(selectedOption) => {
                  setAccountId(selectedOption?.value || '');
                  setAccountName(selectedOption?.label || '');
                }}
                isClearable
                isDisabled={isCreating}
                placeholder="Search for an account..."
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? 'No accounts found' : 'No accounts available'
                }
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '40px',
                    fontSize: '16px',
                  }),
                }}
              />
              {hasSubmitted && errors.accountId && (
                <div className="text-red-500 text-sm mt-1">{errors.accountId}</div>
              )}
            </div>

            {/* Project (optional, depends on account) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <Select
                options={projectOptions}
                value={projectOptions.find(option => option.value === projectId)}
                onChange={(selectedOption) => setProjectId(selectedOption?.value || '')}
                isClearable
                isDisabled={!accountId || isCreating}
                isLoading={isLoadingAccountProjects}
                placeholder={accountId ? "Select a project (optional)..." : "Select an account first..."}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '40px',
                    fontSize: '16px',
                  }),
                }}
              />
              {hasSubmitted && errors.projectId && (
                <div className="text-red-500 text-sm mt-1">{errors.projectId}</div>
              )}
            </div>

            {/* Worktype */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Worktype</label>
              <Select
                options={timecodeOptions}
                value={timecodeOptions.find(option => option.value === timecodeId)}
                onChange={(selectedOption) => setTimecodeId(selectedOption?.value || '')}
                isClearable
                isDisabled={!projectId || isCreating}
                placeholder={projectId ? "Search or select a worktype..." : "Select a project first..."}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '40px',
                    fontSize: '16px',
                  }),
                }}
              />
              {hasSubmitted && errors.timecodeId && (
                <div className="text-red-500 text-sm mt-1">{errors.timecodeId}</div>
              )}
            </div>

            {/* Billing Type Display */}
            {billingtype && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
                <input
                  type="text"
                  value={billingtype}
                  disabled
                  className="w-full p-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const selected = projectTimecodes.find(tc => tc.id === timecodeId);
                  
                  // Check if this is a Non-Billable worktype and user is typing after worktype name
                  if (selected && selected.billingtype === 'Non Billable' && selected.name) {
                    const workTypeName = selected.name;
                    
                    // If current value is just the worktype name and user is adding text
                    if (notes === workTypeName && newValue.length > workTypeName.length && !newValue.includes(' | ')) {
                      // Add " | " separator and the new text
                      const userText = newValue.substring(workTypeName.length);
                      setNotes(`${workTypeName} | ${userText}`);
                      return;
                    }
                  }
                  
                  setNotes(newValue);
                }}
                disabled={isCreating}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                placeholder="Enter notes for all entries..."
              />
              {hasSubmitted && errors.notes && (
                <div className="text-red-500 text-sm mt-1">{errors.notes}</div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-between">
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isCreating}
              className="px-6 py-2 bg-[#537D5D] text-white rounded hover:bg-[#73946B] disabled:opacity-50"
            >
              {isCreating ? 'Creating...' : 'Create Series'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmCreate}
        onCancel={() => setShowConfirmModal(false)}
        title="Create Series Confirmation"
        message={`Are you sure you want to create ${dateCount} time registration${dateCount !== 1 ? 's' : ''} from ${startDate} to ${endDate}?${skipWeekends ? ' (Weekends will be skipped)' : ''}`}
        confirmText="Create All"
        confirmButtonClass="bg-[#537D5D] hover:bg-[#73946B]"
      />
      
      {/* Discard Changes Confirmation Modal */}
      <ConfirmModal
        isOpen={showDiscardModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}